import { DropdownItem } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { DefaultQueryParams, Item, mapResponseToFieldData } from '@features/campaigns/builder/services/formFields.ts';
import { apiRoutes, apiRoutes as detailsApiRoutes } from '@features/campaigns/details/consts/apiRoutes.ts';
import { OneFlightClientListItem } from '@features/campaigns/details/shared/types/oneFlight.ts';
import { apiClient } from '@shared/api/ApiClient.ts';
import { AdManagerApiResponse } from '@shared/api/commonApiInterfaces.ts';
import { CampaignId } from '@shared/types/campaign.ts';

const PER_PAGE_LIMIT = 30;

interface BillingContactsRequiredParams {
  advertiserId: string;
  campaignId: CampaignId;
}

const defaultPageParam: DefaultQueryParams = {
  page: 1,
  search: '',
};

interface BillingContactsRequestParams {
  reqParam: BillingContactsRequiredParams;
  pageParam: DefaultQueryParams;
}

function mapClientsResponseToFieldData(
  response: AdManagerApiResponse<OneFlightClientListItem[]> | null,
): AdManagerApiResponse<DropdownItem[]> | null {
  if (response === null) {
    return response;
  }

  const mappedFields = response.items.map(
    ({ name, id }) =>
      ({
        id,
        name,
        value: id,
        disabled: false,
      }) as const,
  );

  return {
    items: mappedFields,
    meta: response.meta,
  };
}

export async function fetchAgencies({
  pageParam = defaultPageParam,
}): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(apiRoutes.getAgencies(), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
    })
    .then((data) => mapResponseToFieldData(data));
}

export async function fetchBillingContacts({
  reqParam,
  pageParam = defaultPageParam,
}: BillingContactsRequestParams): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;
  const { advertiserId, campaignId } = reqParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(apiRoutes.getBillingContacts(advertiserId), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
      campaignId,
    })
    .then((data) => mapResponseToFieldData(data));
}

export async function fetchOneFlightClients({
  pageParam = defaultPageParam,
}): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<OneFlightClientListItem[]>>(detailsApiRoutes.getOneFlightClients(), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
    })
    .then((data) => mapClientsResponseToFieldData(data));
}

export async function fetchClientBrands(
  clientId: string,
  { pageParam = defaultPageParam },
): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(detailsApiRoutes.getOneFlightClientsBrands(clientId), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
    })
    .then((data) => mapResponseToFieldData(data));
}
